import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { AssociationsAPI, CompaniesAPI } from "@/lib/api";
import { Plus, Pencil, Trash2, ExternalLink, Landmark, Building2 } from "lucide-react";

export default function AdminOrganizations({ type = "association" }) {
  const isCompany = type === "company";
  const apiRef = isCompany ? CompaniesAPI : AssociationsAPI;
  const base = isCompany ? "/admin/companies" : "/admin/associations";
  const publicPath = isCompany ? "/entreprise" : "/association";
  const label = isCompany ? "Entreprises" : "Associations";
  const Icon = isCompany ? Building2 : Landmark;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = () =>
    apiRef.list().then(setItems).catch(() => toast.error("Chargement impossible")).finally(() => setLoading(false));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const remove = async (it) => {
    if (!window.confirm(`Supprimer « ${it.name} » ?`)) return;
    try {
      await apiRef.remove(it.id);
      toast.success("Supprimé");
      load();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight flex items-center gap-3">
            <Icon className="w-7 h-7 text-[#D4AF37]" /> {label}
          </h1>
          <Button
            onClick={() => navigate(`${base}/new`)}
            data-testid={`${type}-new-btn`}
            className="bg-[#D4AF37] text-black hover:bg-[#c9a430] font-bold uppercase tracking-[0.14em] text-xs"
          >
            <Plus className="w-4 h-4 mr-2" /> {isCompany ? "Nouvelle entreprise" : "Nouvelle association"}
          </Button>
        </div>

        {loading ? (
          <div className="text-zinc-500 font-body">Chargement...</div>
        ) : items.length === 0 ? (
          <div className="text-zinc-500 font-body border border-dashed border-white/10 rounded-xl p-10 text-center">
            Aucun profil pour le moment. Crée le premier !
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it) => (
              <div
                key={it.id}
                className="flex items-center gap-4 bg-[#0A0B0E] border border-white/5 rounded-xl p-4"
                data-testid={`${type}-row-${it.slug}`}
              >
                {it.logo_url ? (
                  <img src={it.logo_url} alt="" className="w-12 h-12 object-contain" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center text-[#D4AF37] font-display text-xl">
                    {(it.name || "?").charAt(0)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold uppercase tracking-tight truncate">{it.name}</div>
                  <div className="text-zinc-500 font-body text-xs">
                    {it.founded_year ? `Depuis ${it.founded_year}` : it.subtitle || it.slug}
                    {it.organizer_email ? ` · ${it.organizer_email}` : ""}
                  </div>
                </div>
                <Link to={`${publicPath}/${it.slug}`} className="text-zinc-400 hover:text-white p-2" title="Voir la page publique">
                  <ExternalLink className="w-4 h-4" />
                </Link>
                <Link to={`${base}/${it.id}`} className="text-zinc-400 hover:text-[#D4AF37] p-2" data-testid={`${type}-edit-${it.slug}`}>
                  <Pencil className="w-4 h-4" />
                </Link>
                <button onClick={() => remove(it)} className="text-zinc-400 hover:text-red-400 p-2" data-testid={`${type}-delete-${it.slug}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
